/* Droguería Científica · Historias (Stories)
   Web Component <drogueria-stories>. Vanilla JS, sin dependencias ni globales. */
(() => {
  if (customElements.get('drogueria-stories')) return;

  const SEEN_KEY = 'dcs:seen:v2'; // { [block.id]: huella del contenido (data-version) }
  const LEGACY_SEEN_KEY = 'dcs:seen'; // versión anterior: array de block.id
  const SEEN_MAX = 200;
  const HOLD_MS = 300; // pulsación prolongada: 200 ms confundía toques lentos con pausa y anulaba la navegación
  const SLOP = 10;
  const SWIPE_X = 60;
  const SWIPE_DOWN = 120;
  const SWIPE_UP = 80;
  const POLL_DELAY = 700;
  const MEDIA_TIMEOUT = 8000;
  const MAX_ACTIVE_PREVIEWS = 4; // previews de miniatura reproduciéndose a la vez
  const PREVIEW_RELEASE_MS = 1000; // espera antes de soltar un preview que sale de pantalla (scroll rápido)

  const reduceMq = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopMq = window.matchMedia('(min-width: 750px)');
  const wideMq = window.matchMedia('(min-width: 1100px)');
  const pad = (n) => String(n).padStart(2, '0');
  const saveData = () => Boolean(navigator.connection && navigator.connection.saveData);

  function formatLeft(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(total / 3600);
    if (h > 99) return `${Math.floor(h / 24)}d ${pad(h % 24)}h`;
    return `${pad(h)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  }

  class DrogueriaStories extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this.track = this.querySelector('.dcs-track');
      this.dialog = this.querySelector('.dcs-viewer');
      if (!this.track || !this.dialog) return;
      this._ready = true;
      this._off = [];
      this._timers = {};
      this.raf = 0;
      this.token = 0;
      this.si = -1;
      this.ci = -1;
      this.muted = true;
      this.autoAdvance = this.dataset.autoadvance !== 'false';
      this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.thumbs = [...this.querySelectorAll('.dcs-thumb')];
      const usedHandles = new Set();
      this.stories = this.thumbs.map((btn) => ({
        id: btn.dataset.storyId,
        version: btn.dataset.version || '',
        base: btn.dataset.handle || btn.dataset.storyId,
        handle: btn.dataset.handle || btn.dataset.storyId,
        title: btn.dataset.title || '',
        emoji: btn.dataset.emoji || '',
        profile: btn.dataset.profile || '',
        bg: btn.dataset.bg || '#24316f',
        avatar: btn.dataset.avatar || '',
        cover: btn.dataset.cover || '',
        preview: btn.dataset.previewSrc ? {
          src: btn.dataset.previewSrc,
          start: Math.max(0, Number(btn.dataset.previewStart) || 0),
          len: Number(btn.dataset.previewLength) || 4,
        } : null,
        tpl: btn.dataset.template,
        btn,
      }));
      // La primera historia con un título conserva su handle; las repetidas usan <handle>-<id del bloque>
      this.stories.forEach((st) => {
        if (usedHandles.has(st.handle)) st.handle = `${st.base}-${st.id}`;
        usedHandles.add(st.handle);
      });

      const $ = (s) => this.dialog.querySelector(s);
      this.el = {
        layout: $('.dcs-layout'),
        stage: $('.dcs-stage'),
        frame: $('.dcs-frame'),
        slides: $('.dcs-slides'),
        bars: $('.dcs-bars'),
        avatar: $('.dcs-avatar'),
        avatarImg: $('.dcs-avatar__img'),
        avatarEmoji: $('.dcs-avatar__emoji'),
        title: $('.dcs-head__title'),
        share: $('[data-dcs-action="share"]'),
        sound: $('[data-dcs-action="sound"]'),
        close: $('[data-dcs-action="close"]'),
        toast: $('.dcs-toast'),
        sr: $('.dcs-sr'),
        sides: [...this.dialog.querySelectorAll('.dcs-side, .dcs-foot')],
      };
      this.labels = this.dialog.dataset;

      this._bind();
      this._initCountdown();
      this._initSeen();
      this._pvInit();
      this._updateArrows();

      // Deep link ?story=<handle> (no en el editor de temas)
      if (!this.designMode) {
        let q = null;
        try { q = new URLSearchParams(window.location.search).get('story'); } catch (e) { /* noop */ }
        if (q) {
          let i = this.stories.findIndex((s) => s.handle === q || s.id === q || `${s.base}-${s.id}` === q);
          if (i < 0) i = this.stories.findIndex((s) => s.base === q);
          if (i > -1) this.open(i, this.stories[i].btn, true);
        }
      }
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this._stopLoop();
      Object.values(this._timers).forEach((t) => { clearTimeout(t); clearInterval(t); });
      this._off.forEach((fn) => fn());
      this._off = [];
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this._pvDestroy();
      this._teardownSlides();
      this._unlockScroll();
      if (this.pushed) {
        this.pushed = false;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('story');
          window.history.replaceState(window.history.state, '', url);
        } catch (e) { /* noop */ }
      }
      if (this.dialog && this.dialog.open && typeof this.dialog.close === 'function') {
        this.active = false;
        this.dialog.close();
      }
      this.active = false;
    }

    /* ---------- utilidades ---------- */
    _listen(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this._off.push(() => target.removeEventListener(type, fn, opts));
    }

    _setTimer(name, fn, ms) {
      clearTimeout(this._timers[name]);
      this._timers[name] = setTimeout(fn, ms);
    }

    _emit(name, extra) {
      const s = this.stories[this.si];
      if (!s) return;
      const detail = Object.assign({
        story_id: s.id,
        story_handle: s.handle,
        story_title: s.title,
        slide_index: Math.max(this.ci, 0),
      }, extra);
      document.dispatchEvent(new CustomEvent(name, { detail }));
      try {
        if (window.dataLayer && typeof window.dataLayer.push === 'function') {
          window.dataLayer.push(Object.assign({ event: name }, detail));
        }
      } catch (e) { /* noop */ }
    }

    /* ---------- enlaces y eventos ---------- */
    _bind() {
      const { dialog, el } = this;

      this.thumbs.forEach((btn, i) => this._listen(btn, 'click', () => this.open(i, btn)));

      this.querySelectorAll('[data-dcs-arrow]').forEach((btn) => {
        this._listen(btn, 'click', () => {
          const dir = Number(btn.dataset.dcsArrow);
          this.track.scrollBy({ left: dir * Math.max(this.track.clientWidth * 0.8, 200), behavior: reduceMq.matches ? 'auto' : 'smooth' });
        });
      });
      this.prevArrow = this.querySelector('.dcs-arrow--prev');
      this.nextArrow = this.querySelector('.dcs-arrow--next');
      this._listen(this.track, 'scroll', () => this._updateArrows(), { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this._updateArrows());
        this.resizeObserver.observe(this.track);
      } else {
        this._listen(window, 'resize', () => this._updateArrows());
      }

      // Diálogo
      this._listen(dialog, 'cancel', (e) => { e.preventDefault(); this._close('escape'); });
      this._listen(dialog, 'close', () => this._afterClose());
      this._listen(dialog, 'keydown', (e) => this._onKey(e));
      this._listen(el.layout, 'click', (e) => { if (e.target === el.layout && desktopMq.matches) this._close('backdrop'); });

      this._listen(el.close, 'click', () => this._close('button'));
      this._listen(el.share, 'click', () => this._share());
      this._listen(el.sound, 'click', () => this._toggleSound());
      el.sides.forEach((b) => this._listen(b, 'click', () => {
        const target = this.si + Number(b.dataset.dcsSide);
        if (this.stories[target]) this._showStory(target, 0, Number(b.dataset.dcsSide));
      }));

      // Gestos
      const f = el.frame;
      this._listen(f, 'pointerdown', (e) => this._pDown(e));
      this._listen(f, 'pointermove', (e) => this._pMove(e));
      this._listen(f, 'pointerup', (e) => this._pUp(e));
      this._listen(f, 'pointercancel', () => this._pCancel());
      this._listen(f, 'contextmenu', (e) => e.preventDefault());
      // Foco por teclado o lector de pantalla dentro de la encuesta: pausar hasta que salga del grupo.
      // Con mouse o toque no aplica (:focus-visible), así que se mantienen los 12 s.
      this._listen(f, 'focusin', (e) => {
        const group = e.target instanceof Element ? e.target.closest('.dcs-poll__card') : null;
        if (!group) return;
        let visible = true;
        try { visible = e.target.matches(':focus-visible'); } catch (err) { /* sin soporte: tratar como teclado */ }
        if (!visible || this.focusPause) return;
        this.focusPause = true;
        this._applyPlayback();
      });
      this._listen(f, 'focusout', (e) => {
        if (!this.focusPause) return;
        const group = e.target instanceof Element ? e.target.closest('.dcs-poll__card') : null;
        if (group && e.relatedTarget instanceof Node && group.contains(e.relatedTarget)) return;
        this.focusPause = false;
        this._applyPlayback();
      });
      this._listen(f, 'animationend', () => f.classList.remove('is-enter-next', 'is-enter-prev'));
      this._listen(f, 'click', (e) => {
        const cta = e.target.closest('.dcs-cta');
        if (cta) {
          this._emit('story_cta_click', { cta_url: cta.getAttribute('href'), cta_text: cta.textContent.trim() });
          const newTab = e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button > 0 || cta.target === '_blank';
          if (!newTab) this._leaving();
        }
        const opt = e.target.closest('.dcs-option');
        if (opt) this._pollAnswer(opt);
      });

      // Al cruzar el breakpoint de pantallas anchas, cargar o soltar las vecinas
      const onWide = () => { if (this.active && this.si > -1) this._fillSides(); };
      if (wideMq.addEventListener) this._listen(wideMq, 'change', onWide);

      // Página
      this._listen(document, 'visibilitychange', () => { this.tabHidden = document.hidden; this._applyPlayback(); });
      this._listen(window, 'popstate', () => {
        if (this.dialog.open && this.pushed) { this.pushed = false; this._close('back'); }
      });
      this._listen(window, 'pagehide', () => { this.pushed = false; this._close('pagehide'); });

      // Editor de temas: abrir/enfocar la historia del bloque seleccionado
      this._listen(document, 'shopify:block:select', (e) => {
        const li = e.target instanceof Element ? e.target.closest('.dcs-item') : null;
        if (!li || !this.contains(li)) return;
        const i = [...this.querySelectorAll('.dcs-item')].indexOf(li);
        if (i < 0) return;
        li.scrollIntoView({ block: 'nearest', inline: 'center' });
        if (this.dialog.open) this._showStory(i, 0, 0); else this.open(i, this.stories[i].btn);
      });
      this._listen(document, 'shopify:block:deselect', (e) => {
        if (e.target instanceof Element && this.contains(e.target) && this.dialog.open) this._close('editor');
      });
    }

    /* ---------- previews animados de las miniaturas ----------
       Solo stories con video en el slide 1 y sin portada (data-preview-*). El <video> se crea bajo demanda
       sobre la imagen (que queda como póster y fallback) y se libera al dejar de ser elegible. */
    _pvInit() {
      this.pvBtn = this.querySelector('[data-dcs-previews]');
      this.pvUserPaused = false;
      this.pvBlocked = false;
      this.pvStories = this.stories.filter((s) => s.preview);
      if (!this.pvStories.length || !('IntersectionObserver' in window)) { this.pvStories = []; return; }
      this.pvStories.forEach((st) => { st.pv = { visible: false, failed: false, video: null, win: null, relT: 0, guard: 0, raf: 0 }; });
      this.pvIO = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          const st = this.pvStories.find((s) => s.btn === en.target);
          if (st) st.pv.visible = en.intersectionRatio >= 0.5;
        });
        this._pvSync();
      }, { threshold: [0, 0.5] });
      this.pvStories.forEach((st) => this.pvIO.observe(st.btn));
      if (this.pvBtn) this._listen(this.pvBtn, 'click', () => { this.pvUserPaused = !this.pvUserPaused; this._pvSync(); });
      this._listen(document, 'visibilitychange', () => this._pvSync());
      if (reduceMq.addEventListener) this._listen(reduceMq, 'change', () => this._pvSync());
      const conn = navigator.connection;
      if (conn && conn.addEventListener) this._listen(conn, 'change', () => this._pvSync());
      this._pvUpdateBtn();
    }

    _pvDestroy() {
      if (this.pvIO) { this.pvIO.disconnect(); this.pvIO = null; }
      (this.pvStories || []).forEach((st) => this._pvRelease(st));
    }

    _pvCan() {
      return !reduceMq.matches && !saveData() && !this.active && !document.hidden && !this.pvUserPaused && !this.pvBlocked;
    }

    _pvSync() {
      if (!this.pvIO) return;
      const can = this._pvCan();
      let running = this.pvStories.filter((s) => s.pv.video).length;
      this.pvStories.forEach((st) => {
        const pv = st.pv;
        if (!can) { this._pvRelease(st); return; }
        if (pv.visible) {
          clearTimeout(pv.relT);
          pv.relT = 0;
          if (!pv.video && !pv.failed && running < MAX_ACTIVE_PREVIEWS) { this._pvStart(st); running += 1; }
        } else if (pv.video && !pv.relT) {
          pv.relT = setTimeout(() => { pv.relT = 0; this._pvRelease(st); this._pvSync(); }, PREVIEW_RELEASE_MS);
        }
      });
      this._pvUpdateBtn();
    }

    // El botón global solo se muestra si algún preview puede reproducirse
    _pvUpdateBtn() {
      const b = this.pvBtn;
      if (!b) return;
      const possible = Boolean(this.pvIO) && !this.pvBlocked && !reduceMq.matches && !saveData() && this.pvStories.some((s) => !s.pv.failed);
      b.hidden = !possible;
      b.dataset.state = this.pvUserPaused ? 'b' : 'a';
      b.setAttribute('aria-pressed', String(this.pvUserPaused));
    }

    _pvStart(st) {
      const pv = st.pv;
      const cover = st.btn.querySelector('.dcs-cover');
      if (!cover) return;
      const v = document.createElement('video');
      v.className = 'dcs-cover__video';
      v.muted = true;
      v.defaultMuted = true;
      v.playsInline = true;
      v.preload = 'metadata';
      v.disablePictureInPicture = true;
      v.disableRemotePlayback = true;
      ['muted', 'playsinline', 'disablepictureinpicture', 'disableremoteplayback'].forEach((a) => v.setAttribute(a, ''));
      v.setAttribute('aria-hidden', 'true');
      v.setAttribute('tabindex', '-1');
      pv.video = v;

      v.addEventListener('loadedmetadata', () => {
        const d = v.duration;
        if (!Number.isFinite(d) || d <= 0) { this._pvFail(st, v); return; }
        // Ventana del preview: nunca más larga que el video; si el inicio no deja una ventana completa, se usa la última válida
        const len = Math.min(st.preview.len, d);
        const start = Math.max(0, Math.min(st.preview.start, d - len));
        pv.win = { start, end: start + len };
        if (start > 0) v.currentTime = start;
        this._pvPlay(st, v);
      }, { once: true });
      v.addEventListener('playing', () => {
        clearTimeout(pv.guard);
        v.classList.add('is-playing');
        if (v.requestVideoFrameCallback) {
          const tick = () => { if (pv.video !== v) return; this._pvLoop(st); pv.raf = v.requestVideoFrameCallback(tick); };
          pv.raf = v.requestVideoFrameCallback(tick);
        }
      }, { once: true });
      v.addEventListener('timeupdate', () => this._pvLoop(st));
      v.addEventListener('ended', () => { if (pv.video === v && pv.win) { v.currentTime = pv.win.start; this._pvPlay(st, v); } });
      v.addEventListener('error', () => this._pvFail(st, v), { once: true });

      pv.guard = setTimeout(() => this._pvFail(st, v), MEDIA_TIMEOUT);
      cover.insertBefore(v, cover.querySelector('.dcs-count'));
      v.src = st.preview.src;
    }

    // Loop de la subventana: el atributo loop no permite definir inicio y fin
    _pvLoop(st) {
      const { video: v, win } = st.pv;
      if (v && win && v.currentTime >= win.end - 0.05) v.currentTime = win.start;
    }

    _pvPlay(st, v) {
      if (st.pv.video !== v) return;
      const p = v.play();
      if (p && p.catch) {
        p.catch((err) => {
          if (st.pv.video !== v || (err && err.name === 'AbortError')) return;
          if (err && err.name === 'NotAllowedError') { this.pvBlocked = true; this._pvSync(); } else this._pvFail(st, v);
        });
      }
    }

    _pvFail(st, v) {
      if (st.pv.video !== v) return;
      st.pv.failed = true;
      this._pvRelease(st);
      this._pvSync();
    }

    _pvRelease(st) {
      const pv = st.pv;
      if (!pv) return;
      clearTimeout(pv.relT);
      clearTimeout(pv.guard);
      pv.relT = 0;
      pv.guard = 0;
      pv.win = null;
      const v = pv.video;
      if (!v) return;
      pv.video = null;
      try {
        if (v.cancelVideoFrameCallback && pv.raf) v.cancelVideoFrameCallback(pv.raf);
        v.pause();
        v.removeAttribute('src');
        v.load();
      } catch (e) { /* noop */ }
      pv.raf = 0;
      v.remove();
    }

    /* ---------- carrusel: flechas, vistas, contador ---------- */
    _updateArrows() {
      if (!this.prevArrow || !this.nextArrow) return;
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      const overflow = max > 2;
      this.prevArrow.hidden = !overflow;
      this.nextArrow.hidden = !overflow;
      this.prevArrow.disabled = t.scrollLeft <= 1;
      this.nextArrow.disabled = t.scrollLeft >= max - 1;
    }

    // Devuelve { [block.id]: version }; ante localStorage bloqueado, JSON corrupto o formato inesperado, un objeto vacío
    _readSeen() {
      try {
        const data = JSON.parse(window.localStorage.getItem(SEEN_KEY) || '{}');
        if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
        const seen = {};
        Object.keys(data).forEach((id) => { if (typeof data[id] === 'string' && data[id]) seen[id] = data[id]; });
        return seen;
      } catch (e) { return {}; }
    }

    _writeSeen(seen) {
      if (this.designMode) return; // el Theme Editor no contamina el estado persistente
      try {
        const ids = Object.keys(seen);
        if (ids.length > SEEN_MAX) {
          const here = new Set(this.stories.map((s) => s.id));
          ids.filter((id) => !here.has(id)).slice(0, ids.length - SEEN_MAX).forEach((id) => { delete seen[id]; });
        }
        window.localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
      } catch (e) { /* localStorage no disponible: la sección sigue funcionando */ }
    }

    // dcs:seen (array de ids) → dcs:seen:v2: las historias ya vistas se adoptan con su huella actual; los ids que no están en esta página se descartan
    _migrateSeen() {
      if (this.designMode) return;
      try {
        const ls = window.localStorage;
        if (ls.getItem(SEEN_KEY) !== null) return;
        const legacy = JSON.parse(ls.getItem(LEGACY_SEEN_KEY) || 'null');
        if (!Array.isArray(legacy)) return;
        const seen = {};
        this.stories.forEach((s) => { if (s.version && legacy.includes(s.id)) seen[s.id] = s.version; });
        ls.setItem(SEEN_KEY, JSON.stringify(seen));
        ls.removeItem(LEGACY_SEEN_KEY);
      } catch (e) { /* noop */ }
    }

    _paintSeen(story, isSeen) {
      story.btn.classList.toggle('is-seen', isSeen);
      const label = story.btn.querySelector('[data-dcs-state]');
      const text = isSeen ? this.dataset.stateSeen : this.dataset.stateNew;
      if (label && text) label.textContent = text;
    }

    _initSeen() {
      this._migrateSeen();
      const seen = this._readSeen();
      this.stories.forEach((s) => this._paintSeen(s, Boolean(s.version) && seen[s.id] === s.version));
    }

    _markSeen(story) {
      this._paintSeen(story, true);
      if (!story.version) return;
      const seen = this._readSeen();
      if (seen[story.id] === story.version) return;
      delete seen[story.id];
      seen[story.id] = story.version;
      this._writeSeen(seen);
    }

    _initCountdown() {
      const counts = [...this.querySelectorAll('.dcs-count')];
      if (!counts.length) return;
      const tick = () => {
        const now = Date.now();
        let live = 0;
        counts.forEach((c) => {
          if (c.hidden) return;
          const left = Number(c.dataset.end) - now;
          if (!(left > 0)) { c.hidden = true; return; }
          c.textContent = formatLeft(left);
          live += 1;
        });
        if (!live) clearInterval(this._timers.count);
      };
      tick();
      this._timers.count = setInterval(tick, 1000);
    }

    /* ---------- abrir / cerrar ---------- */
    open(index, opener, fromUrl) {
      if (this.dialog.open || document.querySelector('.dcs-viewer[open]')) return;
      const story = this.stories[index];
      if (!story) return;
      this.opener = opener || story.btn;
      this.active = true;
      this._pvSync();
      this.userPaused = false;
      this.holding = false;
      this.extPause = false;
      this.tabHidden = document.hidden;
      this.closeReason = 'close';

      this._pushHistory(story, fromUrl);
      this._lockScroll();
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');

      this._updateSoundBtn();
      this._showStory(index, 0, 0);
      this.el.close.focus({ preventScroll: true });
    }

    _close(reason) {
      if (!this.dialog.open) return;
      this.closeReason = reason || 'close';
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      this._afterClose();
    }

    _afterClose() {
      if (!this.active) return;
      this.active = false;
      this.pvBlocked = false; // tras una interacción real (abrir/cerrar el visor) se permite un nuevo intento; si vuelve a rechazarse, se bloquea otra vez
      this._pvSync();
      this._stopLoop();
      clearTimeout(this._timers.hold);
      clearTimeout(this._timers.poll);
      clearTimeout(this._timers.media);
      this.token += 1;
      this.focusPause = false;
      this.holding = false;
      this._emit('story_close', { reason: this.closeReason });
      this._teardownSlides();
      this.el.stage.style.transform = '';
      this.el.stage.style.opacity = '';
      this.el.frame.classList.remove('is-enter-next', 'is-enter-prev');
      this.g = null;
      this._unlockScroll();
      if (this.pushed) {
        this.pushed = false;
        try { window.history.back(); } catch (e) { /* noop */ }
      }
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
    }

    _shareUrl(story) {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('story', story.handle);
      return url.toString();
    }

    _pushHistory(story, fromUrl) {
      this.pushed = false;
      if (this.designMode) return;
      try {
        const url = new URL(window.location.href);
        if (fromUrl) { url.searchParams.delete('story'); window.history.replaceState(window.history.state, '', url); }
        window.history.pushState({ dcs: true }, '', this._shareUrl(story));
        this.pushed = true;
      } catch (e) { this.pushed = false; }
    }

    _leaving() {
      if (this.pushed) {
        this.pushed = false;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('story');
          window.history.replaceState(null, '', url);
        } catch (e) { /* noop */ }
      }
      this._unlockScroll();
    }

    _lockScroll() {
      if (this._lock) return;
      const b = document.body;
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      this._lock = { y, position: b.style.position, top: b.style.top, left: b.style.left, right: b.style.right, width: b.style.width, pr: b.style.paddingRight };
      b.style.position = 'fixed';
      b.style.top = `-${y}px`;
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
      if (sbw > 0) b.style.paddingRight = `${sbw}px`;
    }

    _unlockScroll() {
      if (!this._lock) return;
      const b = document.body;
      const l = this._lock;
      this._lock = null;
      b.style.position = l.position;
      b.style.top = l.top;
      b.style.left = l.left;
      b.style.right = l.right;
      b.style.width = l.width;
      b.style.paddingRight = l.pr;
      try {
        window.scrollTo({ top: l.y, left: 0, behavior: 'instant' });
      } catch (e) {
        window.scrollTo(0, l.y);
      }
    }

    /* ---------- historias y slides ---------- */
    _teardownSlides() {
      if (this.slideEls) {
        this.slideEls.forEach((s) => {
          s.querySelectorAll('video').forEach((v) => {
            try {
              v.pause();
              v.querySelectorAll('source').forEach((src) => src.remove());
              v.removeAttribute('src');
              v.load();
            } catch (e) { /* noop */ }
          });
        });
      }
      if (this.el && this.el.slides) this.el.slides.replaceChildren();
      this.slideEls = [];
      this.video = null;
    }

    _showStory(index, slide, dir) {
      const story = this.stories[index];
      if (!story) return;
      clearTimeout(this._timers.poll);
      this._teardownSlides();
      const previous = this.stories[this.si];
      this.si = index;
      this.ci = -1;

      const tpl = document.getElementById(story.tpl);
      if (tpl && tpl.content) this.el.slides.append(tpl.content.cloneNode(true));
      this.slideEls = [...this.el.slides.children];
      if (!this.slideEls.length) { this._next(); return; }

      this.dialog.style.setProperty('--dcs-story-bg', story.bg);
      this.el.title.textContent = story.profile || `${story.emoji} ${story.title}`.trim();
      this._fillAvatar(story);
      this._buildBars(this.slideEls.length);
      this._fillSides();
      this._emit('story_open');

      if (this.pushed) {
        try { window.history.replaceState({ dcs: true }, '', this._shareUrl(story)); } catch (e) { /* noop */ }
      }
      // Transición breve solo al cambiar de perfil; entre historias del mismo perfil (story.base) el cambio es directo
      const f = this.el.frame;
      f.classList.remove('is-enter-next', 'is-enter-prev');
      if (dir && previous && previous.base !== story.base && !reduceMq.matches) {
        void f.offsetWidth;
        f.classList.add(dir > 0 ? 'is-enter-next' : 'is-enter-prev');
      }
      this._showSlide(Math.min(slide, this.slideEls.length - 1));
    }

    _fillAvatar(story) {
      const { avatarImg, avatarEmoji } = this.el;
      if (story.avatar) { avatarImg.src = story.avatar; avatarImg.hidden = false; avatarEmoji.textContent = ''; }
      else { avatarImg.removeAttribute('src'); avatarImg.hidden = true; avatarEmoji.textContent = story.emoji; }
    }

    _fillSides() {
      const wide = wideMq.matches;
      this.el.sides.forEach((b) => {
        const story = this.stories[this.si + Number(b.dataset.dcsSide)];
        b.classList.toggle('is-empty', !story);
        b.tabIndex = story ? 0 : -1;
        if (!story) return;
        const isFoot = b.classList.contains('dcs-foot');
        const img = b.querySelector('img');
        const src = wide ? (isFoot ? story.avatar : story.cover) : '';
        if (src) { img.src = src; img.hidden = false; } else { img.removeAttribute('src'); img.hidden = true; }
        const emoji = b.querySelector(isFoot ? '.dcs-foot__avatar > span' : '.dcs-side__emoji');
        if (emoji) emoji.textContent = src || !wide ? '' : story.emoji;
        const strong = b.querySelector('strong');
        if (strong) strong.textContent = story.title;
        if (!isFoot) b.setAttribute('aria-label', `${Number(b.dataset.dcsSide) < 0 ? this.labels.lPrev : this.labels.lNext}: ${story.title}`);
      });
    }

    _buildBars(count) {
      this.el.bars.replaceChildren();
      this.fills = [];
      for (let i = 0; i < count; i += 1) {
        const bar = document.createElement('span');
        bar.className = 'dcs-bar';
        const fill = document.createElement('span');
        fill.className = 'dcs-bar__fill';
        bar.append(fill);
        this.el.bars.append(bar);
        this.fills.push(fill);
      }
    }

    _setProgress(p) {
      const fill = this.fills[this.ci];
      if (fill) fill.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
    }

    _showSlide(i) {
      this._stopLoop();
      clearTimeout(this._timers.poll);
      clearTimeout(this._timers.media);
      this.ci = i;
      this.token += 1;
      const slide = this.slideEls[i];
      if (i >= this.slideEls.length - 1) this._markSeen(this.stories[this.si]); // vista = se llegó a mostrar el último slide (o el único)
      this.slideEls.forEach((s, k) => {
        s.hidden = k !== i;
        if (k !== i) s.querySelectorAll('video').forEach((v) => { try { v.pause(); } catch (e) { /* noop */ } });
      });
      this.fills.forEach((f, k) => { f.style.transform = `scaleX(${k < i ? 1 : 0})`; });

      this.pollLocked = false;
      this.focusPause = false;
      this.type = slide.dataset.type;
      this.duration = (Number(slide.dataset.duration) || 6) * 1000;
      this.elapsed = 0;
      this.videoFailed = false;
      this.video = slide.querySelector('video');
      this.el.sound.hidden = !this.video;
      if (this.type !== 'video' && !this._needsTimer()) this._setProgress(1);

      const s = this.stories[this.si];
      this.el.sr.textContent = this.dataset.announce
        .replace('#story#', this.si + 1).replace('#total#', this.stories.length)
        .replace('#slide#', i + 1).replace('#slides#', this.slideEls.length)
        + `. ${s.title}`;
      this._emit('story_slide_view');
      this._prepare(slide);
      this._preloadNext();
    }

    get staticMode() { return reduceMq.matches || !this.autoAdvance; }

    _needsTimer() {
      if (this.type === 'video' && !this.videoFailed) return true;
      return !this.staticMode;
    }

    _prepare(slide) {
      this.loading = true;
      slide.classList.add('is-loading');
      const token = this.token;
      const done = () => {
        if (token !== this.token) return;
        clearTimeout(this._timers.media);
        this.loading = false;
        slide.classList.remove('is-loading');
        this._applyPlayback();
      };
      if (this.video) {
        const v = this.video;
        v.muted = this.muted;
        v.preload = 'auto';
        v.addEventListener('loadedmetadata', () => { if (token === this.token && isFinite(v.duration) && v.duration > 0) this.duration = v.duration * 1000; }, { once: true });
        v.addEventListener('canplay', done, { once: true });
        v.addEventListener('ended', () => { if (token === this.token && !this.staticMode) this._next(); }, { once: true });
        v.addEventListener('error', () => { if (token === this.token) { this._videoFail(); done(); } }, { once: true });
        if (v.readyState >= 3) done(); else { try { v.load(); } catch (e) { /* noop */ } }
        this._setTimer('media', () => { if (token === this.token && this.loading) { this._videoFail(); done(); } }, MEDIA_TIMEOUT);
        return;
      }
      const img = slide.querySelector('img');
      if (!img || (img.complete && img.naturalWidth)) { done(); return; }
      img.loading = 'eager';
      const finish = () => done();
      if (img.decode) img.decode().then(finish, finish); else { img.addEventListener('load', finish, { once: true }); img.addEventListener('error', finish, { once: true }); }
      this._setTimer('media', finish, MEDIA_TIMEOUT);
    }

    _preloadNext() {
      const next = this.slideEls[this.ci + 1];
      if (!next) return;
      const img = next.querySelector('img');
      if (img) img.loading = 'eager';
      const v = next.querySelector('video');
      const saveData = Boolean(navigator.connection && navigator.connection.saveData);
      if (v && !saveData && !v.dataset.preloaded) {
        v.dataset.preloaded = '1';
        v.preload = 'auto';
        try { v.load(); } catch (e) { /* noop */ }
      }
    }

    _videoFail() {
      this.videoFailed = true;
      this.type = 'video';
      if (this.video) { try { this.video.pause(); } catch (e) { /* noop */ } }
      this._applyPlayback();
    }

    /* ---------- reproducción y progreso ---------- */
    _isPaused() {
      return Boolean(this.userPaused || this.holding || this.extPause || this.tabHidden || this.loading || this.pollLocked || this.focusPause || !this.active);
    }

    _applyPlayback() {
      if (!this.active || this.ci < 0) return;
      const paused = this._isPaused();
      if (this.video && !this.videoFailed) {
        if (paused) { try { this.video.pause(); } catch (e) { /* noop */ } }
        else this._play(this.video);
      }
      if (!paused && this._needsTimer()) this._startLoop(); else this._stopLoop();
    }

    _play(v) {
      const p = v.play();
      if (p && p.catch) {
        p.catch((err) => {
          if (err && err.name === 'AbortError') return;
          if (!v.muted) { v.muted = true; this.muted = true; this._updateSoundBtn(); this._play(v); } else this._videoFail();
        });
      }
    }

    _startLoop() {
      if (this.raf) return;
      this.last = 0;
      this.raf = window.requestAnimationFrame((t) => this._tick(t));
    }

    _stopLoop() {
      if (this.raf) window.cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    _tick(ts) {
      this.raf = 0;
      if (this._isPaused() || !this._needsTimer()) return;
      const dt = this.last ? Math.min(ts - this.last, 100) : 0;
      this.last = ts;
      let p;
      if (this.type === 'video' && !this.videoFailed) {
        p = this.video && this.video.duration ? this.video.currentTime / this.video.duration : 0;
        if (this.video && this.video.ended) { this._setProgress(1); return; }
      } else {
        this.elapsed += dt;
        p = this.elapsed / this.duration;
      }
      this._setProgress(p);
      if (p >= 1 && !(this.type === 'video' && !this.videoFailed)) { this._next(); return; }
      this.raf = window.requestAnimationFrame((t) => this._tick(t));
    }

    _next() {
      if (this.ci < this.slideEls.length - 1) this._showSlide(this.ci + 1);
      else if (this.si < this.stories.length - 1) this._showStory(this.si + 1, 0, 1);
      else this._close('end');
    }

    _prev() {
      if (this.ci > 0) this._showSlide(this.ci - 1);
      else if (this.si > 0) this._showStory(this.si - 1, 0, -1);
      else this._showSlide(0);
    }

    _toggleUserPause() {
      this.userPaused = !this.userPaused;
      this._applyPlayback();
    }

    _toggleSound() {
      this.muted = !this.muted;
      if (this.video) {
        this.video.muted = this.muted;
        if (!this.muted && this.video.paused && !this._isPaused()) this._play(this.video);
      }
      this._updateSoundBtn();
    }

    _updateSoundBtn() {
      const b = this.el.sound;
      b.dataset.state = this.muted ? 'a' : 'b';
      b.setAttribute('aria-label', this.muted ? this.labels.lUnmute : this.labels.lMute);
    }

    /* ---------- encuesta ---------- */
    _pollAnswer(opt) {
      const poll = opt.closest('.dcs-poll');
      if (!poll || poll.classList.contains('is-locked')) return;
      poll.classList.add('is-locked');
      this.pollLocked = true;
      this._applyPlayback();
      const options = [...poll.querySelectorAll('.dcs-option')];
      options.forEach((o) => o.setAttribute('aria-pressed', String(o === opt)));
      options.forEach((o) => {
        const pct = o.dataset.percent;
        const label = o.querySelector('.dcs-option__pct');
        if (pct !== '' && label) {
          o.querySelector('.dcs-option__fill').style.width = `${Number(pct)}%`;
          label.textContent = `${Number(pct)}%`;
          label.hidden = false;
        }
      });
      this._emit('story_poll_answer', { option_index: Number(opt.dataset.index), option_text: opt.dataset.text });
      const href = opt.dataset.href;
      this._setTimer('poll', () => {
        if (href) { this._leaving(); window.location.assign(href); } else this._next();
      }, POLL_DELAY);
    }

    /* ---------- compartir ---------- */
    async _share() {
      const story = this.stories[this.si];
      const data = { title: `${story.emoji} ${story.title}`.trim(), url: this._shareUrl(story) };
      this.extPause = true;
      this._applyPlayback();
      try {
        if (navigator.share) { await navigator.share(data); return; }
      } catch (e) {
        if (e && e.name === 'AbortError') return;
      } finally {
        this.extPause = false;
        this._applyPlayback();
      }
      let ok = false;
      try { await navigator.clipboard.writeText(data.url); ok = true; } catch (e) {
        const ta = document.createElement('textarea');
        ta.value = data.url;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
        this.dialog.append(ta);
        ta.select();
        try { ok = document.execCommand('copy'); } catch (err) { ok = false; }
        ta.remove();
      }
      if (ok) {
        this.el.toast.textContent = this.labels.lCopied;
        this.el.toast.classList.add('is-visible');
        this._setTimer('toast', () => this.el.toast.classList.remove('is-visible'), 1800);
      }
    }

    /* ---------- teclado y foco ---------- */
    _onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this._close('escape'); return; }
      if (e.key === 'Tab') { this._trapFocus(e); return; }
      const onControl = e.target instanceof Element && e.target.closest('button, a');
      if (e.key === 'ArrowRight') { e.preventDefault(); this._next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this._prev(); }
      else if ((e.key === ' ' || e.key === 'Spacebar') && !onControl) { e.preventDefault(); this._toggleUserPause(); }
    }

    _trapFocus(e) {
      const items = [...this.dialog.querySelectorAll('button:not([disabled]):not([tabindex="-1"]), a[href]')]
        .filter((n) => n.getClientRects().length && !n.closest('[hidden]'));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !this.dialog.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* ---------- gestos (Pointer Events) ---------- */
    _pDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (this.g || e.target.closest('button, a, .dcs-option')) return;
      this.g = { id: e.pointerId, x: e.clientX, y: e.clientY, axis: null, held: false, dx: 0, dy: 0 };
      try { this.el.frame.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
      this._setTimer('hold', () => {
        if (this.g && !this.g.axis) { this.g.held = true; this.holding = true; this._applyPlayback(); }
      }, HOLD_MS);
    }

    _pMove(e) {
      const g = this.g;
      if (!g || g.id !== e.pointerId) return;
      g.dx = e.clientX - g.x;
      g.dy = e.clientY - g.y;
      if (!g.axis && (Math.abs(g.dx) > SLOP || Math.abs(g.dy) > SLOP)) {
        clearTimeout(this._timers.hold);
        g.axis = Math.abs(g.dx) > Math.abs(g.dy) ? 'x' : 'y';
        g.held = false;
        this.holding = true;
        this._applyPlayback();
        this.el.stage.classList.remove('is-settling');
      }
      if (g.axis === 'y' && g.dy > 0) {
        this.el.stage.style.transform = `translateY(${g.dy}px)`;
        this.el.stage.style.opacity = String(Math.max(0.4, 1 - g.dy / 500));
      }
    }

    _pUp(e) {
      const g = this.g;
      if (!g || g.id !== e.pointerId) return;
      this.g = null;
      clearTimeout(this._timers.hold);
      try { this.el.frame.releasePointerCapture(e.pointerId); } catch (err) { /* noop */ }
      this.holding = false;
      if (g.axis === 'y') {
        if (g.dy > SWIPE_DOWN) { this._close('swipe'); return; }
        this._settle();
        const cta = this.slideEls[this.ci] && this.slideEls[this.ci].querySelector('.dcs-cta[data-swipe]');
        if (g.dy < -SWIPE_UP && cta) { this._emit('story_cta_click', { cta_url: cta.getAttribute('href'), cta_text: cta.textContent.trim(), via: 'swipe' }); this._leaving(); window.location.assign(cta.href); return; }
      } else if (g.axis === 'x') {
        if (Math.abs(g.dx) > SWIPE_X) {
          const dir = g.dx < 0 ? 1 : -1;
          if (this.stories[this.si + dir]) { this._showStory(this.si + dir, 0, dir); return; }
        }
      } else if (!g.held) {
        const r = this.el.frame.getBoundingClientRect();
        const ratio = (e.clientX - r.left) / r.width;
        if (ratio < (desktopMq.matches ? 0.5 : 0.3)) this._prev(); else this._next();
        return;
      }
      this._applyPlayback();
    }

    _pCancel() {
      if (!this.g) return;
      this.g = null;
      clearTimeout(this._timers.hold);
      this.holding = false;
      this._settle();
      this._applyPlayback();
    }

    _settle() {
      const s = this.el.stage;
      if (!reduceMq.matches) s.classList.add('is-settling');
      s.style.transform = '';
      s.style.opacity = '';
    }
  }

  customElements.define('drogueria-stories', DrogueriaStories);
})();
